package com.schoolconnect.app.feature.imports

import androidx.compose.ui.graphics.Color
import korlibs.io.file.std.openAsZip
import korlibs.io.stream.openAsync

private data class WorkbookSheetRef(
    val name: String,
    val relationId: String
)

private val ExpectedWorkbookSheets = listOf(
    "Instructions",
    "Subjects",
    "Classes",
    "Staff",
    "Teaching Assignments",
    "Reference Values"
)

private val ExpectedWorkbookHeaders = linkedMapOf(
    "Subjects" to listOf("Subject Code", "Subject Name", "Category"),
    "Classes" to listOf("Class", "Section", "Subject Codes"),
    "Staff" to listOf("Staff ID", "Staff Name", "Staff Role", "Auth Role", "Mobile Number", "Gender", "Email Address"),
    "Teaching Assignments" to listOf("Staff ID", "Staff Name", "Class Teacher For", "Assigned Allocations"),
    "Reference Values" to listOf(
    "",
    "Auth Role",
    "Subject Category",
    "Is Class Teacher",
    "Gender",
    "Class Teacher For",
    "Assigned Allocations"
    )
)

private data class SheetParseResult<T>(
    val rows: List<T>,
    val messages: List<String>
)

private val AssignmentChipPalette = listOf(
    Color(0xFFE2ECFF) to Color(0xFF2563EB),
    Color(0xFFE6F7EE) to Color(0xFF18A765),
    Color(0xFFF0E4FF) to Color(0xFF7C3AED),
    Color(0xFFFFEFE0) to Color(0xFFEA7A18),
    Color(0xFFFFE5EE) to Color(0xFFF43F5E),
    Color(0xFFE4F7F6) to Color(0xFF0EA5B7),
    Color(0xFFE7F7E4) to Color(0xFF65A30D),
    Color(0xFFFFEED6) to Color(0xFFEA7A18)
)

suspend fun parseImportsWorkbook(bytes: ByteArray, fileName: String): Result<ImportsWorkbookPreview> = runCatching {
    val workbook = parseXlsxWorkbookSheets(bytes).getOrElse { throw it }
    val structureErrors = validateWorkbookStructure(workbook)
    if (structureErrors.isNotEmpty()) {
        return@runCatching ImportsWorkbookPreview(
            fileName = fileName.ifBlank { "Import_School_Setup_Template.xlsx" },
            uploadedOn = "Just now",
            staffRecords = emptyList(),
            subjectRecords = emptyList(),
            classRecords = emptyList(),
            assignmentRecords = emptyList(),
            errorRows = emptyList(),
            validationMessages = emptyList(),
            structureErrors = structureErrors
        )
    }
    val sheetMap = workbook.entries.associateBy { normalizeSheetName(it.key) }
    val staff = parseStaffSheet(sheetMap["staff"]?.value.orEmpty())
    val subjects = parseSubjectSheet(sheetMap["subjects"]?.value.orEmpty())
    val classes = parseClassSheet(sheetMap["classes"]?.value.orEmpty())
    val assignments = parseTeachingAssignmentsSheet(sheetMap["teaching assignments"]?.value.orEmpty())

    val validationMessages = buildList {
        addAll(staff.messages)
        addAll(subjects.messages)
        addAll(classes.messages)
        addAll(assignments.messages)
    }.distinct()

    ImportsWorkbookPreview(
        fileName = fileName.ifBlank { "Import_School_Setup_Template.xlsx" },
        uploadedOn = "Just now",
        staffRecords = staff.rows,
        subjectRecords = subjects.rows,
        classRecords = classes.rows,
        assignmentRecords = assignments.rows,
        errorRows = emptyList(),
        validationMessages = validationMessages + if (sheetMap.isEmpty()) listOf("Workbook did not contain any readable sheets.") else emptyList()
    )
}

private fun validateWorkbookStructure(workbook: Map<String, List<List<String>>>): List<String> {
    val issues = mutableListOf<String>()
    val actualSheets = workbook.keys.toList()
    if (actualSheets != ExpectedWorkbookSheets) {
        val missing = ExpectedWorkbookSheets.filterNot { expected -> actualSheets.any { it.equals(expected, ignoreCase = true) } }
        val unexpected = actualSheets.filterNot { actual -> ExpectedWorkbookSheets.any { it.equals(actual, ignoreCase = true) } }
        if (missing.isNotEmpty()) issues += "Missing tab(s): ${missing.joinToString(", ")}."
        if (unexpected.isNotEmpty()) issues += "Unexpected tab(s): ${unexpected.joinToString(", ")}."
        if (missing.isEmpty() && unexpected.isEmpty()) {
            issues += "Tabs are in the wrong order. Required order: ${ExpectedWorkbookSheets.joinToString(" → ")}."
        } else {
            issues += "Required tab order: ${ExpectedWorkbookSheets.joinToString(" → ")}."
        }
    }

    ExpectedWorkbookHeaders.forEach { (sheetName, expectedHeaders) ->
        val sheet = actualSheets.firstOrNull { it.equals(sheetName, ignoreCase = true) } ?: return@forEach
        val actualHeaders = workbook[sheet].orEmpty().firstOrNull().orEmpty().map(String::trim)
        if (actualHeaders != expectedHeaders) {
            issues += "“$sheetName” headers must be: ${expectedHeaders.joinToString(" | ") { it.ifBlank { "(blank first cell)" } }}."
            if (actualHeaders.isEmpty()) issues += "“$sheetName” is missing its header row."
            else issues += "Found: ${actualHeaders.joinToString(" | ") { it.ifBlank { "(blank)" } }}."
        }
    }
    return issues.distinct()
}

private fun parseStaffSheet(rows: List<List<String>>): SheetParseResult<StaffRecordUiModel> {
    val headers = rows.firstOrNull().orEmpty()
    val index = headerIndexMap(headers)
    val messages = mutableListOf<String>()
    val parsedRows = mutableListOf<StaffRecordUiModel>()

    validateHeaders(
        sheetName = "Staff",
        headers = headers,
        expectedHeaders = listOf("Staff ID", "Staff Name", "Staff Role", "Auth Role", "Mobile Number", "Gender", "Email Address"),
        messages = messages
    )

    rows.drop(1).forEachIndexed { rowOffset, row ->
        val excelRow = rowOffset + 2
        val staffId = rowValue(row, index, "staff_id")
        val staffName = rowValue(row, index, "staff_name")
        val staffRole = rowValue(row, index, "staff_role")
        val authRole = rowValue(row, index, "auth_role")
        val mobileNumber = normalizeMobileNumber(rowValue(row, index, "mobile_number"))
        val genderValue = rowValue(row, index, "gender")
        val emailAddress = rowValue(row, index, "email_address")

        if (listOf(staffId, staffName, staffRole, authRole, mobileNumber, genderValue, emailAddress).all { it.isBlank() }) {
            return@forEachIndexed
        }

        parsedRows += StaffRecordUiModel(
            rowNo = excelRow - 1,
            staffId = staffId,
            staffName = staffName,
            gender = genderValue,
            staffRole = staffRole,
            authRole = authRole,
            mobileNumber = mobileNumber,
            emailAddress = emailAddress
        )

    }

    return SheetParseResult(rows = parsedRows, messages = messages)
}

private fun parseSubjectSheet(rows: List<List<String>>): SheetParseResult<SubjectRecordUiModel> {
    val headers = rows.firstOrNull().orEmpty()
    val index = headerIndexMap(headers)
    val messages = mutableListOf<String>()
    val parsedRows = mutableListOf<SubjectRecordUiModel>()

    validateHeaders(
        sheetName = "Subjects",
        headers = headers,
        expectedHeaders = listOf("Subject Code", "Subject Name", "Category"),
        messages = messages
    )

    rows.drop(1).forEachIndexed { rowOffset, row ->
        val excelRow = rowOffset + 2
        val subjectCode = rowValue(row, index, "subject_code")
        val subjectName = rowValue(row, index, "subject_name")
        val category = rowValue(row, index, "category")

        if (listOf(subjectCode, subjectName, category).all { it.isBlank() }) {
            return@forEachIndexed
        }

        parsedRows += SubjectRecordUiModel(
            rowNo = excelRow - 1,
            subjectCode = subjectCode,
            subjectName = subjectName,
            category = category
        )

    }

    return SheetParseResult(rows = parsedRows, messages = messages)
}

private fun parseClassSheet(rows: List<List<String>>): SheetParseResult<ClassRecordUiModel> {
    val headers = rows.firstOrNull().orEmpty()
    val index = headerIndexMap(headers)
    val messages = mutableListOf<String>()
    val parsedRows = mutableListOf<ClassRecordUiModel>()

    validateHeaders(
        sheetName = "Classes",
        headers = headers,
        expectedHeaders = listOf("Class", "Section", "Subject Codes"),
        messages = messages
    )

    rows.drop(1).forEachIndexed { rowOffset, row ->
        val excelRow = rowOffset + 2
        val className = rowValue(row, index, "class")
        val rawSection = rowValue(row, index, "section")
        val rawSubjectCodes = rowValue(row, index, "subject_codes")
        val (section, subjectCodes) = normalizeClassRowValues(
            className = className,
            section = rawSection,
            subjectCodes = rawSubjectCodes
        )

        if (listOf(className, rawSection, rawSubjectCodes).all { it.isBlank() }) {
            return@forEachIndexed
        }

        parsedRows += ClassRecordUiModel(
            rowNo = excelRow - 1,
            className = className,
            section = section,
            subjectCodes = subjectCodes
        )

    }

    return SheetParseResult(rows = parsedRows, messages = messages)
}

private fun normalizeClassRowValues(
    className: String,
    section: String,
    subjectCodes: String
): Pair<String, String> {
    if (subjectCodes.isNotBlank()) return section to subjectCodes

    val looksLikeSubjectCodeList = section.contains('|') && section.split('|').any { it.trim().isNotBlank() }
    if (className.isNotBlank() && looksLikeSubjectCodeList) {
        return "" to section
    }

    return section to subjectCodes
}

private fun parseTeachingAssignmentsSheet(rows: List<List<String>>): SheetParseResult<TeachingAssignmentRecordUiModel> {
    val headers = rows.firstOrNull().orEmpty()
    val index = headerIndexMap(headers)
    val messages = mutableListOf<String>()
    val parsedRows = mutableListOf<TeachingAssignmentRecordUiModel>()

    validateHeaders(
        sheetName = "Teaching Assignments",
        headers = headers,
        expectedHeaders = listOf("Staff ID", "Staff Name", "Class Teacher For", "Assigned Allocations"),
        messages = messages
    )

    rows.drop(1).forEachIndexed { rowOffset, row ->
        val excelRow = rowOffset + 2
        val staffId = rowValue(row, index, "staff_id")
        val staffName = rowValue(row, index, "staff_name")
        val classTeacherFor = rowValue(row, index, "class_teacher_for")
        val assignedAllocations = rowValue(row, index, "assigned_allocations")

        if (listOf(staffId, staffName, classTeacherFor, assignedAllocations).all { it.isBlank() }) {
            return@forEachIndexed
        }

        parsedRows += TeachingAssignmentRecordUiModel(
            rowNo = excelRow - 1,
            staffId = staffId,
            staffName = staffName,
            classTeacherFor = classTeacherFor.ifBlank { "-" },
            allocations = parseAllocationChips(assignedAllocations)
        )

    }

    return SheetParseResult(rows = parsedRows, messages = messages)
}

private fun normalizeMobileNumber(value: String): String {
    val trimmed = value.trim()
    if (trimmed.isEmpty() || trimmed.all { it.isDigit() }) return trimmed

    // XLSX numeric cells can be serialized in scientific notation. Convert
    // only finite, non-negative whole numbers within the exact integer range
    // so validation sees the digits instead of a value such as "9.8E9".
    val numericValue = trimmed.toDoubleOrNull() ?: return trimmed
    if (!numericValue.isFinite() || numericValue < 0.0 || numericValue >= 1_000_000_000_000_000.0) return trimmed
    if (numericValue % 1.0 != 0.0) return trimmed
    return numericValue.toLong().toString()
}

private fun parseAllocationChips(value: String): List<AssignmentChipUiModel> {
    val parts = value.split('|').map { it.trim() }.filter { it.isNotBlank() }
    if (parts.isEmpty()) return emptyList()
    if (parts.size == 1) {
        return listOf(
            AllocationChip(
                label = parts.first()
            )
        )
    }
    return parts.chunked(2).map { pair ->
        val label = if (pair.size == 2) "${pair[0]} : ${pair[1]}" else pair[0]
        AllocationChip(label = label)
    }
}

private fun AllocationChip(label: String): AssignmentChipUiModel {
    val paletteIndex = absoluteValue(label.lowercase().hashCode()) % AssignmentChipPalette.size
    val (background, contentColor) = AssignmentChipPalette[paletteIndex]
    return AssignmentChipUiModel(
        label = label,
        background = background,
        contentColor = contentColor
    )
}

private fun validateHeaders(
    sheetName: String,
    headers: List<String>,
    expectedHeaders: List<String>,
    messages: MutableList<String>
) {
    val normalizedHeaders = headers.map { normalizeHeader(it) }
    val normalizedExpected = expectedHeaders.map { normalizeHeader(it) }
    if (normalizedHeaders.isEmpty()) {
        messages += "$sheetName sheet is empty."
        return
    }
    if (normalizedHeaders.take(expectedHeaders.size) != normalizedExpected) {
        messages += "$sheetName headers do not match the template."
    }
}

private fun headerIndexMap(headers: List<String>): Map<String, Int> {
    val output = linkedMapOf<String, Int>()
    headers.forEachIndexed { index, header ->
        val normalized = normalizeHeader(header)
        if (normalized.isNotBlank() && normalized !in output) {
            output[normalized] = index
        }
    }
    return output
}

private fun rowValue(row: List<String>, index: Map<String, Int>, key: String): String {
    val direct = index[key]
    if (direct != null) {
        return row.getOrNull(direct).orEmpty().trim()
    }
    val aliases = when (key) {
        "staff_id" -> listOf("staffid")
        "staff_name" -> listOf("staffname")
        "staff_role" -> listOf("staffrole")
        "auth_role" -> listOf("authrole")
        "mobile_number" -> listOf("mobilenumber", "mobile")
        "email_address" -> listOf("emailaddress", "email")
        "subject_code" -> listOf("subjectcode")
        "subject_name" -> listOf("subjectname")
        "class" -> listOf("class")
        "subject_codes" -> listOf("subjectcodes")
        "class_teacher_for" -> listOf("classteacherfor")
        "assigned_allocations" -> listOf("assignedallocations")
        "category" -> listOf("category")
        "section" -> listOf("section")
        else -> emptyList()
    }
    aliases.forEach { alias ->
        val aliasIndex = index[alias]
        if (aliasIndex != null) return row.getOrNull(aliasIndex).orEmpty().trim()
    }
    return ""
}

private fun normalizeHeader(value: String): String {
    return value.trim().lowercase().replace(Regex("[^a-z0-9]+"), "_").trim('_')
}

private fun normalizeSheetName(value: String): String {
    return value.trim().lowercase().replace(Regex("\\s+"), " ")
}

private fun absoluteValue(value: Int): Int {
    return if (value == Int.MIN_VALUE) Int.MAX_VALUE else kotlin.math.abs(value)
}

private suspend fun parseXlsxWorkbookSheets(bytes: ByteArray): Result<Map<String, List<List<String>>>> = runCatching {
    val zipRoot = bytes.openAsync().openAsZip()
    try {
        val workbookXml = zipRoot["xl/workbook.xml"].readBytes().decodeToString()
        val workbookRelsXml = zipRoot["xl/_rels/workbook.xml.rels"].readBytes().decodeToString()
        val sharedStringsXml = runCatching { zipRoot["xl/sharedStrings.xml"].readBytes().decodeToString() }.getOrNull()

        val sheetRefs = parseWorkbookSheetRefs(workbookXml)
        val relationTargets = parseWorkbookRelations(workbookRelsXml)
        val sharedStrings = parseSharedStrings(sharedStringsXml)

        val parsed = linkedMapOf<String, List<List<String>>>()
        sheetRefs.forEach { sheet ->
            val target = relationTargets[sheet.relationId] ?: return@forEach
            val sheetPath = normalizeSheetPath(target)
            val sheetXml = runCatching { zipRoot[sheetPath].readBytes().decodeToString() }.getOrNull() ?: return@forEach
            parsed[sheet.name] = parseSheetRows(sheetXml, sharedStrings)
        }
        parsed
    } finally {
        zipRoot.vfs.close()
    }
}

private fun parseWorkbookSheetRefs(xml: String): List<WorkbookSheetRef> {
    val sheetRegex = Regex("<(?:[A-Za-z0-9_]+:)?sheet\\b([^>]*)/?>")
    return sheetRegex.findAll(xml).mapNotNull { match ->
        val attrs = parseAttributes(match.groupValues[1])
        val name = attrs["name"].orEmpty().trim()
        val relationId = (attrs["r:id"] ?: attrs["id"]).orEmpty().trim()
        if (name.isBlank() || relationId.isBlank()) null else WorkbookSheetRef(name, relationId)
    }.toList()
}

private fun parseWorkbookRelations(xml: String): Map<String, String> {
    val relationRegex = Regex("<(?:[A-Za-z0-9_]+:)?Relationship\\b([^>]*)/?>")
    val output = linkedMapOf<String, String>()
    relationRegex.findAll(xml).forEach { match ->
        val attrs = parseAttributes(match.groupValues[1])
        val id = attrs["Id"].orEmpty().trim()
        val target = attrs["Target"].orEmpty().trim()
        if (id.isNotBlank() && target.isNotBlank()) output[id] = target
    }
    return output
}

private fun parseSharedStrings(xml: String?): List<String> {
    if (xml.isNullOrBlank()) return emptyList()
    val siRegex = Regex("<(?:[A-Za-z0-9_]+:)?si\\b[^>]*>([\\s\\S]*?)</(?:[A-Za-z0-9_]+:)?si>", setOf(RegexOption.IGNORE_CASE))
    val textRegex = Regex("<(?:[A-Za-z0-9_]+:)?t\\b[^>]*>([\\s\\S]*?)</(?:[A-Za-z0-9_]+:)?t>", setOf(RegexOption.IGNORE_CASE))
    return siRegex.findAll(xml).map { si ->
        textRegex.findAll(si.groupValues[1]).joinToString("") { unescapeXml(it.groupValues[1]) }.trim()
    }.toList()
}

private fun parseSheetRows(xml: String, sharedStrings: List<String>): List<List<String>> {
    val rows = mutableListOf<List<String>>()
    val rowRegex = Regex("<(?:[A-Za-z0-9_]+:)?row\\b[^>]*>([\\s\\S]*?)</(?:[A-Za-z0-9_]+:)?row>", setOf(RegexOption.IGNORE_CASE))
    // Match self-closing cells first. XLSX commonly represents an empty cell
    // as <c ... />, and dropping that cell shifts every following value into
    // the wrong column (notably Teaching Assignments' blank Class Teacher For).
    val cellRegex = Regex("<(?:[A-Za-z0-9_]+:)?c\\b([^>]*)/>|<(?:[A-Za-z0-9_]+:)?c\\b([^>]*)>([\\s\\S]*?)</(?:[A-Za-z0-9_]+:)?c>", setOf(RegexOption.IGNORE_CASE))
    val valueRegex = Regex("<(?:[A-Za-z0-9_]+:)?v\\b[^>]*>([\\s\\S]*?)</(?:[A-Za-z0-9_]+:)?v>", setOf(RegexOption.IGNORE_CASE))
    val textRegex = Regex("<(?:[A-Za-z0-9_]+:)?t\\b[^>]*>([\\s\\S]*?)</(?:[A-Za-z0-9_]+:)?t>", setOf(RegexOption.IGNORE_CASE))

    rowRegex.findAll(xml).forEach { rowMatch ->
        val rowContent = rowMatch.groupValues[1]
        val rowCells = mutableListOf<String>()
        var activeCol = 0

        cellRegex.findAll(rowContent).forEach { cellMatch ->
            val attrsRaw = if (cellMatch.groupValues[1].isNotBlank()) cellMatch.groupValues[1] else cellMatch.groupValues[2]
            val inner = cellMatch.groupValues[3]
            val attrs = parseAttributes(attrsRaw)
            val type = attrs["t"].orEmpty()
            val col = referenceToColumnIndex(attrs["r"].orEmpty()) ?: activeCol

            val value = if (inner.isBlank()) {
                ""
            } else when (type) {
                "s" -> {
                    val index = valueRegex.find(inner)?.groupValues?.get(1)?.trim()?.toIntOrNull()
                    if (index == null) "" else sharedStrings.getOrNull(index).orEmpty()
                }
                "b" -> {
                    val raw = valueRegex.find(inner)?.groupValues?.get(1)?.trim().orEmpty()
                    if (raw == "1") "TRUE" else "FALSE"
                }
                "inlineStr" -> textRegex.findAll(inner).joinToString("") { unescapeXml(it.groupValues[1]) }.trim()
                else -> unescapeXml(valueRegex.find(inner)?.groupValues?.get(1).orEmpty()).trim()
            }

            while (rowCells.size <= col) rowCells.add("")
            rowCells[col] = value
            activeCol = col + 1
        }

        rows.add(rowCells)
    }
    return rows
}

private fun parseAttributes(raw: String): Map<String, String> {
    if (raw.isBlank()) return emptyMap()
    val attrRegex = Regex("([A-Za-z_][A-Za-z0-9_:\\-.]*)\\s*=\\s*\"([^\"]*)\"")
    val output = linkedMapOf<String, String>()
    attrRegex.findAll(raw).forEach { match ->
        output[match.groupValues[1]] = unescapeXml(match.groupValues[2])
    }
    return output
}

private fun normalizeSheetPath(target: String): String {
    var normalized = target.replace('\\', '/').trim()
    while (normalized.startsWith("../")) normalized = normalized.removePrefix("../")
    normalized = normalized.removePrefix("/")
    return if (normalized.startsWith("xl/")) normalized else "xl/$normalized"
}

private fun referenceToColumnIndex(cellRef: String): Int? {
    if (cellRef.isBlank()) return null
    val letters = cellRef.takeWhile { it.isLetter() }
    if (letters.isBlank()) return null
    var index = 0
    letters.uppercase().forEach { ch ->
        index = index * 26 + (ch.code - 'A'.code + 1)
    }
    return index - 1
}

private fun unescapeXml(value: String): String {
    return value
        .replace("&amp;", "&")
        .replace("&lt;", "<")
        .replace("&gt;", ">")
        .replace("&quot;", "\"")
        .replace("&apos;", "'")
}
